import { useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useWebsiteBuilder } from '../context/WebsiteBuilderContext';
import WebsiteBuilderSidebar from '../components/WebsiteBuilderSidebar';
import WebsitePreview from '../components/WebsitePreview';
import BrandNotificationBell from '../../../components/BrandNotificationBell';
import CreditsWidget from '../../../components/CreditsWidget';
import LogoutButton from '../../../components/LogoutButton';
import LowCreditModal from '../../../components/LowCreditModal';
import CreditDeductionConfirmModal from '../../../components/CreditDeductionConfirmModal';

export default function WebsiteBuilderLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const isPreviewRoute = location.pathname === '/website-builder/preview';
  const isOnboardingRoute = location.pathname === '/website-builder/onboarding';
  const {
    currentStep, handleStepChange, handleNext, handleBack, isExported, projectState,
    brandName, brandLogo, saveStatus, lastSavedAt, handleManualSave,
    showSessionModal, setShowSessionModal, showLockedModal, setShowLockedModal,
    showNoSessionModal, setShowNoSessionModal, setCurrentStep,
    isCheckingSession, globalLoading, loadingText,
    handleStartFresh, handleRestoreSession, triggerChargeConfirm,
    showDeductionConfirmModal, setShowDeductionConfirmModal,
    handleConfirmDeduction, modalCredits, isDeducting,
    modalOpen, setModalOpen, modalRequired, modalIsBlocker,
    showPreview, setShowPreview, theme, pages, globalContactInfo, activePageId,
    isOnboardingCompleted,
  } = useWebsiteBuilder();

  // Route gating: if onboarding is not completed, redirect to onboarding screen
  useEffect(() => {
    if (isOnboardingCompleted === false && !isOnboardingRoute && !isPreviewRoute) {
      navigate('/website-builder/onboarding', { replace: true });
    }
  }, [isOnboardingCompleted, isOnboardingRoute, isPreviewRoute, navigate]);

  // If on the onboarding screen, render full-bleed without wizard sidebar or chrome
  if (isOnboardingRoute) {
    return <Outlet />;
  }

  return (
    <div className="h-screen overflow-hidden bg-[#f5f3ef] flex font-body relative" style={{ fontFamily: "'DM Sans', sans-serif" }}>

      {/* Session Modal */}
      {!isPreviewRoute && showSessionModal && (
        <div className="absolute inset-0 bg-black/40 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 animate-fade-in text-center">
            <div className="w-16 h-16 bg-navy/5 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-history-line text-2xl text-navy"></i>
            </div>
            <h3 className="font-serif text-2xl text-navy mb-2">Previous Session Found</h3>
            <p className="text-gray-600 mb-8">We found your previous website draft. Would you like to restore it to save time, or start completely fresh?</p>
            <div className="flex gap-4 justify-center">
              <button onClick={handleStartFresh} className="px-6 py-2.5 border border-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors">Start Fresh</button>
              <button onClick={() => handleRestoreSession()} className="px-6 py-2.5 bg-[#0859b8] text-white font-medium rounded-lg hover:bg-[#0859b8]/90 transition-colors shadow-lg shadow-[#0859b8]/20 flex items-center gap-2">
                <i className="ri-refresh-line"></i>Restore Session
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Locked Modal */}
      {!isPreviewRoute && showLockedModal && (
        <div className="absolute inset-0 bg-black/40 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 animate-fade-in text-center">
            <div className="w-16 h-16 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-lock-line text-2xl text-red-500"></i>
            </div>
            <h3 className="font-serif text-2xl text-navy mb-2">Project Locked</h3>
            <p className="text-gray-600 mb-8 text-sm leading-relaxed">This website project has been downloaded and is locked permanently. You cannot make any further changes. To build a new website, start fresh (costs 5,000 credits).</p>
            <div className="flex flex-col gap-3">
              <button onClick={() => triggerChargeConfirm('start-fresh')} className="w-full px-6 py-2.5 bg-[#0859b8] text-white font-medium rounded-lg hover:bg-[#0859b8]/90 transition-colors shadow-lg shadow-[#0859b8]/20 flex items-center justify-center gap-2">
                <i className="ri-refresh-line"></i>Start Fresh (Cost: 5,000 credits)
              </button>
              <div className="flex gap-3">
                <button onClick={() => navigate('/marketing-dashboard')} className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors">Go Home</button>
                <button onClick={() => { setShowLockedModal(false); setCurrentStep(4); navigate('/website-builder/export'); }} className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors">View Download</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* No Session Modal */}
      {!isPreviewRoute && showNoSessionModal && (
        <div className="absolute inset-0 bg-black/40 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 animate-fade-in text-center">
            <div className="w-16 h-16 bg-navy/5 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-information-line text-2xl text-navy"></i>
            </div>
            <h3 className="font-serif text-2xl text-navy mb-2">Build New Website</h3>
            <p className="text-gray-600 mb-8 text-sm leading-relaxed">Creating a new website requires 5,000 credits, charged upfront. Top up your balance or proceed to get started.</p>
            <div className="flex gap-4 justify-center">
              <button onClick={() => navigate('/marketing-dashboard')} className="px-6 py-2.5 border border-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors">Cancel</button>
              <button onClick={() => triggerChargeConfirm('new-site')} className="px-6 py-2.5 bg-[#0859b8] text-white font-medium rounded-lg hover:bg-[#0859b8]/90 transition-colors shadow-lg shadow-[#0859b8]/20 flex items-center gap-2">Pay & Start Building</button>
            </div>
          </div>
        </div>
      )}

      {!isPreviewRoute && (
        <CreditDeductionConfirmModal
          isOpen={showDeductionConfirmModal}
          onClose={() => setShowDeductionConfirmModal(false)}
          onConfirm={handleConfirmDeduction}
          currentCredits={modalCredits}
          deductCredits={5000}
          isProcessing={isDeducting}
        />
      )}

      {!isPreviewRoute && isCheckingSession && (
        <div className="fixed inset-0 bg-white/70 z-[100] flex flex-col items-center justify-center backdrop-blur-sm">
          <div className="w-12 h-12 border-4 border-[#0859b8]/20 border-t-[#0859b8] rounded-full animate-spin"></div>
          <p className="mt-4 font-serif text-lg text-navy animate-pulse">Checking for previous session...</p>
        </div>
      )}

      {!isPreviewRoute && globalLoading && (
        <div className="fixed inset-0 bg-white/70 z-[100] flex flex-col items-center justify-center backdrop-blur-sm">
          <div className="w-12 h-12 border-4 border-[#0859b8]/20 border-t-[#0859b8] rounded-full animate-spin"></div>
          <p className="mt-4 font-serif text-lg text-navy animate-pulse">{loadingText}</p>
        </div>
      )}

      {/* Left Sidebar */}
      <WebsiteBuilderSidebar currentStep={currentStep} onStepChange={handleStepChange} isExported={isExported} />

      {/* Main Canvas */}
      <main className="flex-1 flex flex-col min-w-0 h-full">
        {/* Top Bar */}
        <header className="h-[88px] bg-white border-b border-gray-200 flex items-center justify-between px-6 flex-shrink-0">
          <div className="flex items-center gap-1.5 text-sm text-gray-500">
            <button onClick={() => navigate('/marketing-dashboard')} className="hover:text-[#0859b8] transition-colors cursor-pointer">Home</button>
            <i className="ri-arrow-right-s-line text-gray-300 text-xs"></i>
            <span className="text-navy font-medium flex items-center gap-2">Website Builder</span>
          </div>

          <div className="flex items-center gap-3">
            <BrandNotificationBell />
            <CreditsWidget />
            <button onClick={() => navigate('/profile')} className="flex items-center gap-2 pl-1 pr-3 py-1 rounded-full hover:bg-gray-100 transition-colors cursor-pointer group" title="Profile">
              {brandLogo ? (
                <div className="w-8 h-8 rounded-full overflow-hidden bg-white border border-gray-200 flex items-center justify-center flex-shrink-0">
                  <img src={brandLogo.replace(/"/g, '')} alt="Business Logo" className="w-full h-full object-cover" />
                </div>
              ) : (
                <div className="w-8 h-8 bg-navy/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-navy font-semibold text-sm">{brandName.charAt(0).toUpperCase()}</span>
                </div>
              )}
              <span className="text-sm font-medium text-gray-700 group-hover:text-gray-900 whitespace-nowrap" title="Profile">Profile</span>
              <i className="ri-arrow-right-s-line text-gray-400 text-base"></i>
            </button>
            <LogoutButton className="flex items-center gap-2 px-3 py-2 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 transition-all">
              <i className="ri-logout-box-r-line text-[18px] text-slate-600"></i>
              <span className="hidden lg:inline text-sm font-semibold text-slate-700">Logout</span>
            </LogoutButton>
            <button
              onClick={() => {
                if (currentStep > 1 || projectState === 'Draft' || projectState === 'Downloaded') {
                  navigate('/website-builder/preview');
                }
              }}
              disabled={currentStep === 1 && projectState === 'None'}
              className={`text-sm font-medium px-4 py-2 rounded-lg transition-colors flex items-center gap-2 whitespace-nowrap ${currentStep === 1 && projectState === 'None' ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-[#0859b8] hover:bg-[#0859b8]/90 text-white cursor-pointer'}`}
            >
              <i className="ri-eye-line"></i>Preview Output
            </button>
          </div>
        </header>

        {/* Step Content via Outlet */}
        <div className="flex-1 px-6 pb-6 pt-6 overflow-hidden flex flex-col bg-[#fdfdfc]">
          <Outlet />
        </div>

        {/* Bottom Action Bar */}
        {currentStep < 4 && (
          <div className="h-16 bg-white border-t border-gray-200 flex items-center justify-between px-6 flex-shrink-0">
            <button
              onClick={handleBack}
              disabled={currentStep === 1}
              className={`flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap ${currentStep === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-100'}`}
            >
              <i className="ri-arrow-left-line"></i>Back
            </button>

            {currentStep >= 2 && !isExported && (
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  {saveStatus === 'saving' && (
                    <>
                      <div className="w-3.5 h-3.5 border-2 border-[#0859b8]/30 border-t-[#0859b8] rounded-full animate-spin"></div>
                      <span className="text-[#0859b8] font-medium">Saving changes...</span>
                    </>
                  )}
                  {saveStatus === 'saved' && (
                    <>
                      <i className="ri-checkbox-circle-fill text-emerald-500 text-sm"></i>
                      <span className="text-emerald-700 font-medium">All changes saved {lastSavedAt ? `(${lastSavedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })})` : ''}</span>
                    </>
                  )}
                  {saveStatus === 'unsaved' && (
                    <>
                      <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse"></span>
                      <span className="text-amber-700 font-medium">Unsaved changes (auto-saving in 5s)</span>
                    </>
                  )}
                  {saveStatus === 'idle' && (
                    <>
                      <i className="ri-checkbox-circle-line text-gray-400 text-sm"></i>
                      <span className="text-gray-500">All changes saved</span>
                    </>
                  )}
                </div>
                <button
                  onClick={() => handleManualSave()}
                  disabled={saveStatus === 'saving'}
                  title="Save current progress"
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-200 rounded-lg transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <i className="ri-save-3-line text-slate-600 text-sm"></i>
                  <span>Save Progress</span>
                </button>
              </div>
            )}

            <div className="flex items-center gap-3">
              <span className="text-xs text-gray-400">Step {currentStep} of 4</span>
              <button
                onClick={handleNext}
                className="flex items-center gap-2 bg-[#0859b8] hover:bg-[#0859b8]/90 text-white text-sm font-medium px-5 py-2.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                {currentStep === 3 ? 'Finish & Download' : 'Continue'}
                <i className="ri-arrow-right-line"></i>
              </button>
            </div>
          </div>
        )}

        {/* Website Preview Overlay */}
        {showPreview && (
          <WebsitePreview
            onClose={() => setShowPreview(false)}
            theme={theme}
            pages={pages}
            brandName={brandName}
            brandLogo={brandLogo}
            globalContactInfo={globalContactInfo}
            initialPageId={activePageId}
          />
        )}
      </main>

      <LowCreditModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        currentCredits={modalCredits}
        requiredCredits={modalRequired}
        isBlocker={modalIsBlocker}
      />
    </div>
  );
}
